import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { FormTag, RawTag, TagOption } from 'types/entities/smartTags/tag.type';

interface UseOcrTagsOptions {
    defaultTags: Ref<RawTag[]>;
    formTags: Ref<FormTag[]>;
}

interface UseOcrTagsReturn {
    tagsResults: Ref<TagOption[]>;
    tagsLoading: Ref<boolean>;
    tagsSearchQuery: Ref<string>;
    tagDefaultValues: ComputedRef<TagOption[]>;
    onTagsSearch: (query: string) => void;
    tagsUpdated: (tags: RawTag[]) => void;
}

const normalizeTagOptions = (tags: RawTag[]): TagOption[] =>
    (tags || []).map((tag) => {
        const id = (tag.id ?? tag.value) as number | string;
        const label = String(tag.value ?? tag.label ?? '');
        return { id, value: id, label };
    });

export const useOcrTags = (options: UseOcrTagsOptions): UseOcrTagsReturn => {
    const tagsResults = ref<TagOption[]>([]);
    const tagsLoading = ref(false);
    const tagsSearchQuery = ref('');

    watch(
        options.defaultTags,
        (defaultTags) => {
            tagsResults.value = normalizeTagOptions(defaultTags);
        },
        { immediate: true },
    );

    const tagDefaultValues = computed<TagOption[]>(() =>
        options.formTags.value.map((tag) => {
            const id = (tag.id ?? `new:${tag.value}`) as number | string;
            const label = String(tag.value ?? '');
            return { id, value: id, label };
        }),
    );

    const onTagsSearch = (query: string): void => {
        tagsSearchQuery.value = (query || '').toString();
        const normalizedQuery = tagsSearchQuery.value.trim().toLowerCase();
        const tagsToUse = normalizedQuery
            ? options.defaultTags.value.filter((tag) =>
                String(tag.value ?? '')
                    .toLowerCase()
                    .includes(normalizedQuery),
            )
            : options.defaultTags.value;
        tagsResults.value = normalizeTagOptions(tagsToUse);
    };

    const tagsUpdated = (tags: RawTag[]): void => {
        options.formTags.value = (tags || []).map((tag) => {
            const rawValue = tag.label ?? tag.value;
            const id =
                tag.id ??
                (typeof tag.value === 'number' || typeof tag.value === 'string' ? tag.value : null);
            return { id, value: String(rawValue ?? '') };
        });
    };

    return {
        tagsResults,
        tagsLoading,
        tagsSearchQuery,
        tagDefaultValues,
        onTagsSearch,
        tagsUpdated,
    };
};
